import React from 'react';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../context';
import { useAlert, useFetch, useToggle } from '../../hooks';
import {
    Form,
    Field,
    FormType,
    InputEventProps,
    ActionButton,
    Alert
} from '..';

interface ChangePasswordProps {
    closeHandler: () => void;
}

export interface ChangePasswordValuesProps {
    OldPassword: string;
    Password: string;
    ConfirmPassword: string;
}
const changePasswordInitialValues: ChangePasswordValuesProps = {
    OldPassword: '',
    Password: '',
    ConfirmPassword: ''
};
export const ChangePassword: React.FC<ChangePasswordProps> = React.memo(
    ({ closeHandler }) => {
        const { session } = useAuth();
        const [fetchHandler, loading] = useFetch(
            '/dbEmployeeEditDetails',
            'PUT'
        );
        const { alert, dispatchAlert, closeAlert } = useAlert();
        const [changePasswordValues, setChangePasswordValues] =
            React.useState<ChangePasswordValuesProps>(
                changePasswordInitialValues
            );
        const [showOldPassword, toggleOldPassword] = useToggle(false);
        const [showNewPassword, toggleNewPassword] = useToggle(false);

        const onChangeHandler = React.useCallback(
            (key: keyof ChangePasswordValuesProps) => (e: InputEventProps) =>
                setChangePasswordValues(prevChangePasswordValues => {
                    return {
                        ...prevChangePasswordValues,
                        [key]: e.target.value
                    };
                }),
            []
        );

        const successHandler = React.useCallback(() => {
            closeHandler();
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: 'Success!',
                severity: 'success'
            });
        }, [closeHandler, dispatchAlert]);
        const errorHandler = React.useCallback(
            message => {
                dispatchAlert({
                    type: 'UPDATE_ALERT',
                    message: message,
                    severity: 'error'
                });
            },
            [dispatchAlert]
        );

        const changePasswordHandler = React.useCallback(
            async (e: FormType) => {
                e.preventDefault();
                const result = await fetchHandler({
                    data: {
                        member_guid: session.MemberGuid,
                        session_guid: session.SessionGuid,
                        data: changePasswordValues
                    }
                });
                result.success ? successHandler() : errorHandler(result.msg);
                dispatchAlert({ type: 'OPEN' });
            },
            [
                fetchHandler,
                session.MemberGuid,
                session.SessionGuid,
                changePasswordValues,
                successHandler,
                errorHandler,
                dispatchAlert
            ]
        );

        return (
            <Form onSubmit={changePasswordHandler}>
                <Alert closeHandler={closeAlert} {...alert} />
                <Grid item xs={12}>
                    <h1
                        style={{
                            fontSize: '2rem',
                            marginBottom: '2rem'
                        }}
                    >
                        Change Password
                    </h1>
                </Grid>{' '}
                <Field
                    name='OldPassword'
                    label='Old Password'
                    onChange={onChangeHandler('OldPassword')}
                    endAdornment={
                        <IconButton onClick={toggleOldPassword}>
                            {showOldPassword ? (
                                <VisibilityIcon />
                            ) : (
                                <VisibilityOffIcon />
                            )}
                        </IconButton>
                    }
                />
                <Field
                    name='Password'
                    label='New Password'
                    onChange={onChangeHandler('Password')}
                    endAdornment={
                        <IconButton onClick={toggleNewPassword}>
                            {showNewPassword ? (
                                <VisibilityIcon />
                            ) : (
                                <VisibilityOffIcon />
                            )}
                        </IconButton>
                    }
                />
                <Field
                    name='ConfirmPassword'
                    label='Confirm Password'
                    onChange={onChangeHandler('ConfirmPassword')}
                    endAdornment={
                        <IconButton onClick={toggleNewPassword}>
                            {showNewPassword ? (
                                <VisibilityIcon />
                            ) : (
                                <VisibilityOffIcon />
                            )}
                        </IconButton>
                    }
                />
                <ActionButton
                    type='submit'
                    loading={loading}
                    title='Submit'
                    variant='contained'
                    sm={6}
                    grid
                />
                <ActionButton
                    type='button'
                    loading={loading}
                    title='Submit'
                    sm={6}
                    onClick={closeHandler}
                    grid
                />
            </Form>
        );
    }
);
